/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import type { ApiTitle } from "@lumi/data";
import { authenticatedFetch } from "@lumi/data";
import { Bookmark, ChevronDown, ChevronUp, Type } from "lucide-react";
import type { ReactElement } from "react";
import React, { useCallback } from "react";
import { useLoaderData } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { createRefreshCallback } from "../../auth/refreshCallback";
import type { BrowseLoaderData } from "../../loaders/browseLoader";
import { useMapContext } from "../../map/contexts/MapContext";
import { EmptyState } from "../../shared/components";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { countTitlesByCountry, normalizeCountryName } from "../../utils/countryMapping";
import { normalizeLanguageName } from "../../utils/languageMapping";
import { toggleCountryLabels } from "../../utils/mapbox/countryLabels";
import { CollectionRail } from "../home/components/CollectionRail";
import type { FilterState } from "./components/FilterPanel";
import { FilterPanel } from "./components/FilterPanel";
import { useFilterPanel } from "./contexts/FilterPanelContext";
import { useFilterState } from "./hooks/useFilterState";

interface GroupedTitles {
  groupName: string;
  titles: ApiTitle[];
}

/** Check if title passes year range filter */
function passesYearFilter(title: ApiTitle, yearRange: [number, number]): boolean {
  if (title.year === null || title.year === undefined) return true;
  return title.year >= yearRange[0] && title.year <= yearRange[1];
}

/** Check if title passes country filter */
function passesCountryFilter(title: ApiTitle, country: string | null): boolean {
  if (!country) return true;
  const normalized = title.country ? normalizeCountryName(title.country) : null;
  return normalized === country;
}

/** Check if title passes genre filter */
function passesGenreFilter(title: ApiTitle, genre: string | null): boolean {
  if (!genre) return true;
  return title.genres?.includes(genre) ?? false;
}

/** Check if title passes director filter */
function passesDirectorFilter(title: ApiTitle, director: string | null): boolean {
  if (!director) return true;
  return title.directorName === director;
}

/** Check if title passes language filter */
function passesLanguageFilter(title: ApiTitle, language: string | null): boolean {
  if (!language) return true;
  return title.languages?.some((lang) => normalizeLanguageName(lang) === language) ?? false;
}

/** Check if title passes watchlist filter */
function passesWatchlistFilter(title: ApiTitle, watchlistOnly: boolean, watchlistTitleIds: Set<string>): boolean {
  if (!watchlistOnly) return true;
  return watchlistTitleIds.has(title.id);
}

/**
 * Check if a title passes all filter criteria
 * Returns false if title should be filtered out, true if it passes
 */
function passesTitleFilter(title: ApiTitle, filterState: FilterState, watchlistTitleIds: Set<string>): boolean {
  return (
    passesYearFilter(title, filterState.yearRange) &&
    passesCountryFilter(title, filterState.country) &&
    passesGenreFilter(title, filterState.genre) &&
    passesDirectorFilter(title, filterState.director) &&
    passesLanguageFilter(title, filterState.language) &&
    passesWatchlistFilter(title, filterState.watchlistOnly, watchlistTitleIds)
  );
}

function groupTitlesByCountry(titles: ApiTitle[]): GroupedTitles[] {
  const groups = new Map<string, ApiTitle[]>();

  for (const title of titles) {
    // Use the country field which stores the primary production country
    const country = title.country;

    if (country && typeof country === "string") {
      const normalized = normalizeCountryName(country);
      if (!groups.has(normalized)) {
        groups.set(normalized, []);
      }
      groups.get(normalized)?.push(title);
    }
  }

  // Convert to array and sort by country name
  return Array.from(groups.entries())
    .map(([groupName, titleList]) => ({
      groupName,
      titles: titleList,
    }))
    .sort((a, b) => a.groupName.localeCompare(b.groupName));
}

export function GroupedBrowsePage(): ReactElement {
  // Get pre-loaded public titles from route loader
  const loaderData = useLoaderData() as BrowseLoaderData;

  const [titles] = React.useState<ApiTitle[]>(loaderData.titles);
  const [watchlistTitleIds, setWatchlistTitleIds] = React.useState<Set<string>>(new Set());
  const [watchlistLoading, setWatchlistLoading] = React.useState(false);
  const [isRailExpanded, setIsRailExpanded] = React.useState(true); // Start expanded to show results immediately
  const [labelsVisible, setLabelsVisible] = React.useState(false); // Country labels toggle state
  const { isOpen: isFilterPanelOpen, close: closeFilterPanel } = useFilterPanel();
  const { transitionTo } = useTransitionNavigation();
  const { accessToken, refreshAuth } = useAuth();
  const { setMapContext, map, isMapLoaded } = useMapContext();

  const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

  // Stable navigation handler (prevent event handler orphaning during View Transitions)
  const handleNavigateBrowse = useCallback(() => {
    transitionTo("/home/browse");
  }, [transitionTo]);

  // Calculate year bounds from titles
  const yearBounds = React.useMemo(() => {
    const years = titles.map((t) => t.year).filter((y): y is number => y !== null && y !== undefined);
    if (years.length === 0) return { min: 1900, max: new Date().getFullYear() };
    return {
      min: Math.min(...years),
      max: Math.max(...years),
    };
  }, [titles]);

  // Get available countries from titles
  const availableCountries = React.useMemo(() => {
    const countrySet = new Set<string>();
    for (const title of titles) {
      if (title.country && typeof title.country === "string") {
        const normalized = normalizeCountryName(title.country);
        countrySet.add(normalized);
      }
    }
    return Array.from(countrySet).sort((a, b) => a.localeCompare(b));
  }, [titles]);

  // Get available genres from titles
  const availableGenres = React.useMemo(() => {
    const genreSet = new Set<string>();
    for (const title of titles) {
      if (title.genres && Array.isArray(title.genres)) {
        for (const genre of title.genres) {
          if (genre) genreSet.add(genre);
        }
      }
    }
    return Array.from(genreSet).sort((a, b) => a.localeCompare(b));
  }, [titles]);

  // Get available directors from titles
  const availableDirectors = React.useMemo(() => {
    const directorSet = new Set<string>();
    for (const title of titles) {
      if (title.directorName) {
        directorSet.add(title.directorName);
      }
    }
    return Array.from(directorSet).sort((a, b) => a.localeCompare(b));
  }, [titles]);

  // Get available languages from titles
  const availableLanguages = React.useMemo(() => {
    const languageSet = new Set<string>();
    for (const title of titles) {
      if (title.languages && Array.isArray(title.languages)) {
        for (const lang of title.languages) {
          if (lang) {
            // Normalize language codes to full names (e.g., "en" -> "English")
            const normalized = normalizeLanguageName(lang);
            languageSet.add(normalized);
          }
        }
      }
    }
    return Array.from(languageSet).sort((a, b) => a.localeCompare(b));
  }, [titles]);

  // Filter state management with URL sync
  const { filterState, updateFilters, clearFilters, hasActiveFilters } = useFilterState(yearBounds);

  // Fetch watchlist title IDs for authenticated users
  React.useEffect(() => {
    async function fetchWatchlist(): Promise<void> {
      if (!accessToken) {
        setWatchlistTitleIds(new Set());
        return;
      }

      setWatchlistLoading(true);
      try {
        const response = await authenticatedFetch<{ items: Array<{ title: ApiTitle }> }>(
          `${API_BASE}/v1/watchlist`,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        // Extract title IDs into a Set for O(1) lookup
        const titleIds = new Set(response.items.map((item) => item.title.id));
        setWatchlistTitleIds(titleIds);
      } catch (err) {
        console.error("Failed to fetch watchlist:", err);
        setWatchlistTitleIds(new Set());
      } finally {
        setWatchlistLoading(false);
      }
    }

    void fetchWatchlist();
  }, [accessToken, API_BASE, refreshAuth]);

  // Group titles by country (default view)
  const allGroupedTitles = React.useMemo(() => {
    return groupTitlesByCountry(titles);
  }, [titles]);

  // Apply advanced filters (year, country, genre, director, language from filter panel)
  // This is used for BOTH the rail display AND markers
  const advancedFilteredTitles = React.useMemo(() => {
    // Always use allGroupedTitles as source - filterState.country handles country filtering
    const flattened = allGroupedTitles.flatMap((group) => group.titles);

    return flattened.filter((title) => passesTitleFilter(title, filterState, watchlistTitleIds));
  }, [allGroupedTitles, filterState, watchlistTitleIds]);

  // Separate filter for rail display - includes titleId filter
  const railFilteredTitles = React.useMemo(() => {
    if (!filterState.titleId) {
      return advancedFilteredTitles;
    }

    // If titleId is set, show only that specific title
    return advancedFilteredTitles.filter((title) => title.id === filterState.titleId);
  }, [advancedFilteredTitles, filterState.titleId]);

  // Always flatten to single horizontal rail - uses railFilteredTitles which includes titleId filter
  const displayContent = React.useMemo(() => {
    const label = (() => {
      // Build label from active filters
      if (filterState.watchlistOnly && filterState.country) {
        return `${filterState.country} Watchlist`;
      }
      if (filterState.watchlistOnly) {
        return "My Watchlist";
      }
      if (filterState.country) {
        return filterState.country;
      }
      return "All Countries";
    })();

    return {
      titles: railFilteredTitles,
      label,
    };
  }, [railFilteredTitles, filterState.country, filterState.watchlistOnly]);

  // Count titles by country for map visualization
  const countryCounts = React.useMemo(() => countTitlesByCountry(titles), [titles]);

  const handleCountryClick = React.useCallback(
    (countryName: string | null): void => {
      // Country click clears title filter and sets country filter
      // Click filters are mutually exclusive - only one active at a time
      if (countryName === null || filterState.country === countryName) {
        updateFilters({ country: null, titleId: null });
      } else {
        updateFilters({ country: countryName, titleId: null });
      }
    },
    [filterState.country, updateFilters],
  );

  const handleTitleClick = React.useCallback(
    (titleId: string | null): void => {
      // Title click clears country filter and sets title filter
      // Click filters are mutually exclusive - only one active at a time
      if (titleId === null) {
        // Clear both filters (from background click or deselect)
        updateFilters({ titleId: null, country: null });
      } else if (filterState.titleId === titleId) {
        // Deselect if clicking same title marker (clear both to be safe)
        updateFilters({ titleId: null, country: null });
      } else {
        // Filter by title, clear country click filter
        updateFilters({ titleId, country: null });
      }
    },
    [filterState.titleId, updateFilters],
  );

  const handleToggleLabels = React.useCallback((): void => {
    console.log("[handleToggleLabels] map:", map, "isMapLoaded:", isMapLoaded, "labelsVisible:", labelsVisible);
    if (map && isMapLoaded) {
      const newState = !labelsVisible;
      console.log("[handleToggleLabels] Toggling labels to:", newState);
      setLabelsVisible(newState);
      toggleCountryLabels(map, newState);
    } else {
      console.warn("[handleToggleLabels] Map not available or not loaded");
    }
  }, [map, isMapLoaded, labelsVisible]);

  // Update map context whenever our state changes
  // Build a short, human-readable summary of the active filters for tooltips
  const markerFilterSummary = React.useMemo(() => {
    const parts = [
      `Country: ${filterState.country ?? "All"}`,
      `Genre: ${filterState.genre ?? "All"}`,
      `Director: ${filterState.director ?? "All"}`,
      `Language: ${filterState.language ?? "All"}`,
      `Year: ${filterState.yearRange[0]}-${filterState.yearRange[1]}`,
    ];
    return parts.join(" • ");
  }, [filterState.country, filterState.genre, filterState.director, filterState.language, filterState.yearRange]);

  React.useEffect(() => {
    setMapContext({
      countryCounts,
      selectedCountry: filterState.country,
      selectedTitleId: filterState.titleId,
      onCountryClick: handleCountryClick,
      onTitleClick: handleTitleClick,
      markerTitles: advancedFilteredTitles, // Pass filtered titles (without titleId filter) for markers
      markerFilterSummary,
    });
  }, [
    countryCounts,
    filterState.country,
    filterState.titleId,
    handleCountryClick,
    handleTitleClick,
    advancedFilteredTitles,
    markerFilterSummary,
    setMapContext,
  ]);

  // Keyboard shortcut: Up/Down to toggle rail expansion
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;

      // Ignore if typing in input field
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      // Ignore if filter panel is open (to allow navigation in dropdowns)
      if (isFilterPanelOpen) {
        return;
      }

      // Up/Down arrow to toggle rail
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        setIsRailExpanded((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFilterPanelOpen]);

  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      {/* Map renders in RootLayout (fixed background) - this page just shows through */}

      {/* Filter Panel - Floating top-left */}
      <FilterPanel
        isOpen={isFilterPanelOpen}
        onClose={closeFilterPanel}
        filterState={filterState}
        onFilterChange={updateFilters}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        availableCountries={availableCountries}
        availableGenres={availableGenres}
        availableDirectors={availableDirectors}
        availableLanguages={availableLanguages}
        yearBounds={yearBounds}
        isAuthenticated={!!accessToken}
        watchlistCount={watchlistTitleIds.size}
        watchlistLoading={watchlistLoading}
      />

      {/* Spacer - takes available space above rail, lets map show through */}
      <div className="flex-1 w-full" />

      {/* Content Rail - Fixed to bottom, collapsible elevator pattern */}
      {/* pointer-events-auto to re-enable interactions (parent has pointer-events-none for map) */}
      <div className="flex-shrink-0 content-rails pointer-events-auto">
        {/* Compact context bar with chevron and labels button - side by side */}
        <div className="flex items-center gap-3 px-6 sm:px-8 py-3 bg-neutral-900/0 pointer-events-auto">
          {/* Compact context bar with chevron */}
          <div className="flex items-center gap-3 border border-lumi-accent/50 rounded-md px-4 py-2 mb-4 bg-black/40 backdrop-blur-md">
            {/* Chevron button */}
            <button
              onClick={() => setIsRailExpanded(!isRailExpanded)}
              className="text-lumi-accent hover:text-white transition-colors cursor-pointer pointer-events-auto"
              aria-label={isRailExpanded ? "Collapse content" : "Expand content"}
              type="button"
            >
              {isRailExpanded ? (
                <ChevronDown size={20} className="sm:w-5 sm:h-5" />
              ) : (
                <ChevronUp size={20} className="sm:w-5 sm:h-5" />
              )}
            </button>

            {/* Context info */}
            <span className="text-sm sm:text-base text-lumi-accent font-medium">{displayContent.label}</span>
            <span className="text-sm sm:text-base text-neutral-100">
              {displayContent.titles.length} film{displayContent.titles.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Country labels toggle button - positioned to the right */}
          <div className="flex items-center border border-lumi-accent/50 rounded-md px-2 py-2 mb-4 bg-black/40 backdrop-blur-md">
            <button
              onClick={handleToggleLabels}
              className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${
                labelsVisible ? "bg-lumi-accent text-black" : "text-neutral-400 hover:text-lumi-accent"
              }`}
              aria-label="Toggle country names"
              title="Toggle country names"
              type="button"
            >
              <Type size={16} />
            </button>
          </div>
        </div>

        {/* Rail container with transform animation - only this part slides up/down */}
        <div
          className="transition-all duration-300 ease-in-out overflow-hidden"
          style={{
            height: isRailExpanded ? "420px" : "0px",
          }}
        >
          <div className="h-[420px] flex flex-col justify-center py-6 sm:py-8 px-0 bg-black/20 backdrop-blur-md border-t border-neutral-800 overflow-hidden">
            {displayContent.titles.length > 0 ? (
              <CollectionRail
                title="" // No heading - context is in the bar above
                items={displayContent.titles}
                cardType="HORIZONTAL"
                transitionTo={transitionTo}
              />
            ) : (
              <EmptyState
                icon={<Bookmark className="w-16 h-16" />}
                title={filterState.watchlistOnly ? "Your watchlist is empty" : "No content available"}
                message={getEmptyStateMessage(filterState.watchlistOnly, filterState.country, displayContent.label)}
                action={
                  filterState.watchlistOnly
                    ? {
                        label: "Explore Content",
                        onClick: handleNavigateBrowse,
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Get appropriate empty state message based on context
 */
function getEmptyStateMessage(watchlistOnly: boolean, selectedCountry: string | null, label: string): string {
  if (watchlistOnly) {
    return "Add titles you want to watch later to see them here.";
  }
  if (selectedCountry) {
    return `No content available from ${label}.`;
  }
  return "No content available in this collection.";
}
